import React, { useState, useEffect } from 'react';
import {
  Button,
  Container,
  FormControl,
  InputLabel,
  Box,
  Modal,
  Tooltip,
  MenuItem,
  Select,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';

const EditCertificateIssue = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [candidateName, setCandidateName] = useState('');
  const [certificateName, setCertificateName] = useState('');
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const navigate = useNavigate();
  const { issueId } = useParams();

  // Static data for certificates and candidates
  const staticCertificateData = [
    { certificateId: 1, certificateName: 'Certificate of Completion' },
    { certificateId: 2, certificateName: 'Advanced Diploma' },
    { certificateId: 3, certificateName: 'Professional Certification' },
    { certificateId: 4, certificateName: 'Training Certificate' },
    { certificateId: 5, certificateName: 'Workshop Certificate' },
    { certificateId: 6, certificateName: 'Online Course Certificate' },
  ];

  const staticCandidateData = [
    { candidateId: 1, candidateName: 'John Doe' },
    { candidateId: 2, candidateName: 'Jane Smith' },
    { candidateId: 3, candidateName: 'Alice Johnson' },
    { candidateId: 4, candidateName: 'Bob Brown' },
    { candidateId: 5, candidateName: 'Charlie Davis' },
    { candidateId: 6, candidateName: 'Eve Wilson' },
  ];

  const staticCertificateIssueData = [
    { issueId: 1, candidateName: 'John Doe', certificateName: 'Certificate of Completion', issueDate: '2023-01-15', expiryDate: '2024-01-15' },
    { issueId: 2, candidateName: 'Jane Smith', certificateName: 'Advanced Diploma', issueDate: '2022-05-20', expiryDate: '2023-05-20' },
    { issueId: 3, candidateName: 'Alice Johnson', certificateName: 'Professional Certification', issueDate: '2023-03-10', expiryDate: '2024-03-10' },
    { issueId: 4, candidateName: 'Bob Brown', certificateName: 'Training Certificate', issueDate: '2022-11-05', expiryDate: '2023-11-05' },
    { issueId: 5, candidateName: 'Charlie Davis', certificateName: 'Workshop Certificate', issueDate: '2023-06-30', expiryDate: '2024-06-30' },
    { issueId: 6, candidateName: 'Eve Wilson', certificateName: 'Online Course Certificate', issueDate: '2023-07-22', expiryDate: '2024-07-22' },
  ];

  useEffect(() => {
    // Auto-fill the form with the certificate issue data based on the issueId
    const certificateIssue = staticCertificateIssueData.find(issue => issue.issueId === parseInt(issueId));
    if (certificateIssue) {
      setCandidateName(certificateIssue.candidateName);
      setCertificateName(certificateIssue.certificateName);
      setIssueDate(certificateIssue.issueDate);
      setExpiryDate(certificateIssue.expiryDate);
    }
  }, [issueId]);

  const handleSave = (e) => {
    e.preventDefault();
    if (candidateName && certificateName && issueDate && expiryDate) {
      setShowSuccessModal(true);
    } else {
      alert('Please fill all fields');
    }
  };

  const handleCancel = () => {
    // Reset to the original certificate issue data
    const certificateIssue = staticCertificateIssueData.find(issue => issue.issueId === parseInt(issueId));
    if (certificateIssue) {
      setCandidateName(certificateIssue.candidateName);
      setCertificateName(certificateIssue.certificateName);
      setIssueDate(certificateIssue.issueDate);
      setExpiryDate(certificateIssue.expiryDate);
    }
  };

  return (
    <Container>
      <Box
        sx={{
          backgroundColor: 'rgba(2, 78, 101, 1)',
          padding: '12px 16px',
          boxShadow: 3,
          borderRadius: '4px 4px 0 0',
          margin: '0 auto',
          width: "100%",
        }}
      >
        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
          Edit Certificate Issue
        </Typography>
      </Box>
      <Paper elevation={3} style={{ padding: 16, width: '100%', margin: 'auto', marginTop: 6, backgroundColor: '#f8fdfeff' }}>
        <div>
          <FormControl fullWidth sx={{ my: 2 }}>
            <InputLabel htmlFor="candidate">Candidate Name:</InputLabel>
            <Select
              id="candidate"
              value={candidateName}
              label="Candidate Name"
              onChange={(e) => setCandidateName(e.target.value)}
            >
              {staticCandidateData.map((candidate) => (
                <MenuItem key={candidate.candidateId} value={candidate.candidateName}>
                  {candidate.candidateName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ my: 2 }}>
            <InputLabel htmlFor="certificate">Certificate Name:</InputLabel>
            <Select
              id="certificate"
              value={certificateName}
              label="Certificate Name"
              onChange={(e) => setCertificateName(e.target.value)}
            >
              {staticCertificateData.map((certificate) => (
                <MenuItem key={certificate.certificateId} value={certificate.certificateName}>
                  {certificate.certificateName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Issue Date"
            type="date"
            variant="outlined"
            fullWidth
            value={issueDate}
            onChange={(e) => setIssueDate(e.target.value)}
            sx={{ my: 2 }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Expiry Date"
            type="date"
            variant="outlined"
            fullWidth
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            sx={{ my: 2 }}
            InputLabelProps={{ shrink: true }}
          />
          <Tooltip title={!candidateName ? "Select Candidate" : !certificateName ? "Select Certificate" : !issueDate ? "Select Issue Date" : !expiryDate ? "Select Expiry Date" : ""} arrow>
            <span>
              <Button
                variant="contained"
                onClick={handleSave}
                sx={{
                  mr: 2,
                  backgroundColor: '#127498ff',
                  '&:hover': { backgroundColor: '#116280ff' }
                }}
                disabled={!candidateName || !certificateName || !issueDate || !expiryDate}
              >
                SAVE
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancel}
          >
            RESET
          </Button>
        </div>
      </Paper>
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Certificate Issue Updated ✅
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              Certificate Issue Updated Successfully
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1);
                }}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      
    </Container>
  );
};

export default EditCertificateIssue;
